import { z } from 'zod';
export const TYPE_PAIRS = ['plex', 'inter', 'source-serif', 'atkinson'];
export const COVER_STYLES = ['classic', 'banner', 'centered'];
export const THEME_LOCALES = ['en-US', 'en-GB', 'en-CA', 'en-AU', 'en-NZ', 'en-IE'];
export const FOOTER_TEXT_MAX_CHARS = 120;
export const PREPARED_BY_MAX_CHARS = 120;
export const CLOSING_NOTE_MAX_CHARS = 1000;
// Escape sequences, never raw characters, for U+2028 and U+2029.
const SINGLE_LINE = /^[^\r\n\u2028\u2029]*$/;
/** Single-line text: rejects an interior CR, LF, U+2028 or U+2029. .trim() runs
 *  first, so leading and trailing breaks are stripped, not rejected. */
const oneLine = (max) => z.string().trim().max(max).regex(SINGLE_LINE, 'Must be a single line');
// Field schemas declared ONCE with no .default(): ThemeSchema and
// ThemePatchSchema are both built from themeFields, because .partial() keeps
// each field's default (packages/core/src/operations.ts:113-127).
const themeFields = {
    typePair: z.enum(TYPE_PAIRS),
    cover: z.enum(COVER_STYLES),
    blockNumbers: z.boolean(),
    footerText: oneLine(FOOTER_TEXT_MAX_CHARS),
    preparedBy: oneLine(PREPARED_BY_MAX_CHARS),
    closingNote: z.string().trim().max(CLOSING_NOTE_MAX_CHARS),
    locale: z.enum(THEME_LOCALES)
};
export const ThemeSchema = z.object(themeFields).strict();
export const THEME_KEYS = Object.keys(themeFields);
export const ThemePatchSchema = z.object(themeFields).partial().strict();
export const DEFAULT_THEME = {
    typePair: 'plex',
    cover: 'classic',
    blockNumbers: true,
    footerText: '',
    preparedBy: '',
    closingNote: '',
    locale: 'en-US'
};
/** Reads a stored value field by field; a bad or missing field falls back to
 *  its default alone, unknown keys are dropped, and nothing throws. */
export function readStoredTheme(stored) {
    const src = typeof stored === 'object' && stored !== null && !Array.isArray(stored)
        ? stored
        : {};
    const out = { ...DEFAULT_THEME };
    for (const key of THEME_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(src, key))
            continue;
        const parsed = themeFields[key].safeParse(src[key]);
        if (parsed.success)
            out[key] = parsed.data;
    }
    return out;
}
export function resolveTheme(stored, ent) {
    return ent.customTheme ? stored : { ...DEFAULT_THEME, locale: stored.locale };
}
export const TYPE_PAIR_FONTS = {
    plex: {
        label: 'IBM Plex',
        body: "'IBM Plex Sans', system-ui, sans-serif",
        mono: "'IBM Plex Mono', ui-monospace, monospace"
    },
    inter: {
        label: 'Inter',
        body: "'Inter', system-ui, sans-serif",
        mono: "'JetBrains Mono', ui-monospace, monospace"
    },
    'source-serif': {
        label: 'Source Serif',
        body: "'Source Serif 4', Georgia, serif",
        mono: "'Source Code Pro', ui-monospace, monospace"
    },
    atkinson: {
        label: 'Atkinson Hyperlegible',
        body: "'Atkinson Hyperlegible', system-ui, sans-serif",
        mono: "'Atkinson Hyperlegible Mono', ui-monospace, monospace"
    }
};
export function themeFontStacks(theme) {
    return TYPE_PAIR_FONTS[theme.typePair];
}
const firstFamily = (stack) => (stack.split(',')[0] ?? '').trim().replace(/^'|'$/g, '');
/** First family of each stack, unquoted. For test assertions only: never pass
 *  this to `document.fonts.load()`, which takes a CSS font shorthand. */
export function themeFontFamilies(theme) {
    const s = themeFontStacks(theme);
    return { body: firstFamily(s.body), mono: firstFamily(s.mono) };
}
//# sourceMappingURL=theme.js.map